// Copyright 2025, University of Maryland, All Rights Reserved
import { memo, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu } from 'lucide-react';
import '@universityofmaryland/utilityheader';

import { useAuth } from '../context/AuthContext';
import { useOrganizations } from '../hooks/use-organizations';
import { useOrgWorkspaces } from '../hooks/use-workspaces';
import { useMediaQuery } from '../hooks/use-media-query';
import { OrganizationDropdown } from './OrganizationDropdown';
import { WorkspaceSwitcher } from './WorkspaceSwitcher';
import { ProfileMenu } from './ProfileMenu';
import { WorkspaceWizard } from './templates';
import { useBooleanState } from '../hooks/use-boolean-state';
import type { OrganizationResponse, WorkspaceInfo } from '../types/workspace';

export interface NavigationItem {
  path: string;
  label: string;
  icon?: string;
  adminOnly?: boolean;
  activeWhen?: string[]; // Additional paths that should show this item as active
}

/** Auth state for testing - mirrors useAuth return type */
export interface AppHeaderAuthState {
  isGlobalAdmin?: boolean;
  isAuthenticated?: boolean;
  accessibleOrganizations?: OrganizationResponse[];
  selectedOrganizationId?: string | null;
  selectedWorkspaceId?: string | null;
  selectOrganization?: (orgId: string) => void;
  selectWorkspace?: (orgId: string, wsId: string) => void;
  canCreateWorkspace?: boolean;
}

export interface AppHeaderProps {
  appId: string;
  navigationItems: NavigationItem[];
  debugMode?: boolean;
  /** Optional auth state for testing - overrides useAuth hook */
  authState?: AppHeaderAuthState;
  /** Optional organizations data for testing - overrides useOrganizations hook */
  organizations?: OrganizationResponse[];
  /** Optional workspaces data for testing - overrides useOrgWorkspaces hook */
  workspaces?: WorkspaceInfo[];
}

export const AppHeader = memo(function AppHeader({
  appId,
  navigationItems,
  debugMode = false,
  authState: injectedAuth,
  organizations: injectedOrgs,
  workspaces: injectedWorkspaces,
}: AppHeaderProps) {
  const location = useLocation();

  // Auth state - always call hook, use injected if provided
  const hookAuth = useAuth();
  const {
    isGlobalAdmin = false,
    isAuthenticated = false,
    accessibleOrganizations = [],
    selectedOrganizationId,
    selectedWorkspaceId,
    selectOrganization,
    selectWorkspace,
    canCreateWorkspace = false,
  } = injectedAuth ?? hookAuth;

  // Data fetching - always call hooks, use injected if provided
  const { data: hookOrgs = [] } = useOrganizations();
  const { data: hookWorkspaces = [], isLoading: hookWorkspacesLoading } = useOrgWorkspaces(selectedOrganizationId);
  const organizations = injectedOrgs ?? hookOrgs;
  const workspaces = injectedWorkspaces ?? hookWorkspaces;
  const workspacesLoading = injectedWorkspaces ? false : hookWorkspacesLoading;

  // Responsive layout - secure-analysis uses higher threshold
  const threshold = appId === 'secure-analysis' ? 1536 : 1235;
  const isMobile = useMediaQuery('(max-width: 639px)');
  const isTwoRowLayout = useMediaQuery(`(min-width: 640px) and (max-width: ${threshold - 1}px)`);

  // UI state
  const [mobileMenuOpen, toggleMobileMenu, closeMobileMenu] = useBooleanState(false);
  const [appSelectorOpen, openAppSelector, closeAppSelector] = useBooleanState(false);

  // Show org selector if admin OR multiple orgs
  const showOrgSelector = isGlobalAdmin || accessibleOrganizations.length > 1;

  // Filter nav items by admin flag
  const visibleNavItems = navigationItems.filter(
    (item) => !item.adminOnly || isGlobalAdmin
  );

  // Check if path is active
  const isActive = useCallback(
    (path: string, activeWhen?: string[]) => {
      const pathsToCheck = [path, ...(activeWhen ?? [])];
      return pathsToCheck.some(
        (p) => location.pathname === p || location.pathname.startsWith(p)
      );
    },
    [location.pathname]
  );

  const handleWorkspaceCreated = useCallback(
    (orgId: string, wsId: string) => {
      closeAppSelector();
      selectWorkspace?.(orgId, wsId);
    },
    [closeAppSelector, selectWorkspace]
  );

  return (
    <>
      <umd-utility-header size="100%" padding="20px" />
      <nav className="bg-white shadow-sm">
        <div className="mx-auto px-4">
          <div className="flex h-16 items-center justify-between">
            {isMobile ? (
              <MobileHeader
                mobileMenuOpen={mobileMenuOpen}
                onToggleMenu={toggleMobileMenu}
                isGlobalAdmin={isGlobalAdmin}
                appId={appId}
              />
            ) : (
              <DesktopHeader
                isAuthenticated={isAuthenticated}
                showOrgSelector={showOrgSelector}
                organizations={organizations}
                selectedOrganizationId={selectedOrganizationId ?? null}
                onOrganizationSelect={selectOrganization ?? (() => {})}
                workspaces={workspaces}
                workspacesLoading={workspacesLoading}
                selectedWorkspaceId={selectedWorkspaceId ?? null}
                onWorkspaceSelect={selectWorkspace ?? (() => {})}
                isGlobalAdmin={isGlobalAdmin}
                canCreateWorkspace={canCreateWorkspace}
                onCreateWorkspace={openAppSelector}
                visibleNavItems={isTwoRowLayout ? [] : visibleNavItems}
                isActive={isActive}
                onNavClick={closeMobileMenu}
                appId={appId}
              />
            )}
          </div>

          {/* Two-row layout: nav links in second row */}
          {isTwoRowLayout && (
            <div className="mx-auto px-4 py-2 flex items-center space-x-8 overflow-x-auto">
              <NavLinks
                items={visibleNavItems}
                isActive={isActive}
                onClick={closeMobileMenu}
              />
            </div>
          )}

          {/* Mobile menu dropdown */}
          {isMobile && mobileMenuOpen && (
            <div className="pt-2 pb-3 space-y-1">
              <NavLinks
                items={visibleNavItems}
                isActive={isActive}
                onClick={closeMobileMenu}
                mobile
              />
            </div>
          )}
        </div>
      </nav>

      {/* Application selector modal for workspace creation */}
      {selectedOrganizationId && (
        <WorkspaceWizard
          app_id={appId}
          open={appSelectorOpen}
          onOpenChange={closeAppSelector}
          orgId={selectedOrganizationId}
          onWorkspaceCreated={handleWorkspaceCreated}
          debug={debugMode}
        />
      )}
    </>
  );
});

// --- Extracted Components (module-level, not nested) ---

interface MobileHeaderProps {
  mobileMenuOpen: boolean;
  onToggleMenu: () => void;
  isGlobalAdmin: boolean;
  appId: string;
}

const MobileHeader = memo(function MobileHeader({
  mobileMenuOpen,
  onToggleMenu,
  isGlobalAdmin,
  appId,
}: MobileHeaderProps) {
  return (
    <>
      <button
        type="button"
        className="inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:bg-gray-100"
        onClick={onToggleMenu}
        aria-expanded={mobileMenuOpen}
        aria-label="Toggle navigation menu"
      >
        <Menu className="h-6 w-6" aria-hidden="true" />
      </button>
      <ProfileMenu isGlobalAdmin={isGlobalAdmin} app_id={appId} />
    </>
  );
});

interface DesktopHeaderProps {
  isAuthenticated: boolean;
  showOrgSelector: boolean;
  organizations: OrganizationResponse[];
  selectedOrganizationId: string | null;
  onOrganizationSelect: (orgId: string) => void;
  workspaces: WorkspaceInfo[];
  workspacesLoading: boolean;
  selectedWorkspaceId: string | null;
  onWorkspaceSelect: (orgId: string, wsId: string) => void;
  isGlobalAdmin: boolean;
  canCreateWorkspace: boolean;
  onCreateWorkspace: () => void;
  visibleNavItems: NavigationItem[];
  isActive: (path: string, activeWhen?: string[]) => boolean;
  onNavClick: () => void;
  appId: string;
}

const DesktopHeader = memo(function DesktopHeader({
  isAuthenticated,
  showOrgSelector,
  organizations,
  selectedOrganizationId,
  onOrganizationSelect,
  workspaces,
  workspacesLoading,
  selectedWorkspaceId,
  onWorkspaceSelect,
  isGlobalAdmin,
  canCreateWorkspace,
  onCreateWorkspace,
  visibleNavItems,
  isActive,
  onNavClick,
  appId,
}: DesktopHeaderProps) {
  return (
    <>
      <div className="flex items-center space-x-8">
        {isAuthenticated && showOrgSelector && (
          <OrganizationDropdown
            accessibleOrganizations={organizations}
            selectedOrganizationId={selectedOrganizationId ?? undefined}
            selectOrganization={onOrganizationSelect}
          />
        )}
        {isAuthenticated && (
          <WorkspaceSwitcher
            accessibleWorkspaces={workspaces}
            selectedOrganizationId={selectedOrganizationId ?? undefined}
            selectedWorkspaceId={selectedWorkspaceId ?? undefined}
            selectWorkspace={onWorkspaceSelect}
            isLoading={workspacesLoading}
            canCreateWorkspace={canCreateWorkspace}
            onCreateWorkspace={onCreateWorkspace}
          />
        )}
        <NavLinks items={visibleNavItems} isActive={isActive} onClick={onNavClick} />
      </div>
      <div className="flex-shrink-0">
        <ProfileMenu isGlobalAdmin={isGlobalAdmin} app_id={appId} />
      </div>
    </>
  );
});

interface NavLinksProps {
  items: NavigationItem[];
  isActive: (path: string, activeWhen?: string[]) => boolean;
  onClick: () => void;
  mobile?: boolean;
}

const NavLinks = memo(function NavLinks({ items, isActive, onClick, mobile }: NavLinksProps) {
  const baseClass = mobile
    ? 'block px-3 py-2 text-base font-medium'
    : 'inline-flex items-center px-3 py-2 text-sm font-medium rounded-md';

  return (
    <>
      {items.map((item) => (
        <Link
          key={item.path}
          to={item.path}
          className={`${baseClass} ${isActive(item.path, item.activeWhen) ? 'bg-gray-200' : ''}`}
          onClick={onClick}
        >
          {item.icon && <span className="mr-1">{item.icon}</span>}
          {item.label}
        </Link>
      ))}
    </>
  );
});
